import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaPhone, FaEnvelope, FaChevronDown } from 'react-icons/fa';
import { IconWrapper } from '../icons/IconWrapper';
import Button from '../ui/Button';
import { DefaultTheme } from 'styled-components';
import QuoteBadge from '../ui/QuoteBadge';
import Logo from '../ui/Logo';
import MetalPriceTicker from '../ui/MetalPriceTicker';

interface NavItem {
  label: string;
  path: string;
  children?: NavItem[];
}

const MAIN_NAV_ITEMS: NavItem[] = [
  {
    label: 'Products',
    path: '/products'
  },
  {
    label: 'Industries',
    path: '/industries'
  },
  {
    label: 'About Us',
    path: '/about'
  },
  /* 
  { 
    label: 'Sustainability', 
    path: '/sustainability',
    children: [
      { label: 'Environmental Policy', path: '/sustainability/environmental' },
      { label: 'Social Responsibility', path: '/sustainability/social' },
      { label: 'Governance', path: '/sustainability/governance' },
    ]
  },
  { 
    label: 'Our Difference', 
    path: '/difference',
    children: [
      { label: 'Innovation', path: '/difference/innovation' },
      { label: 'Quality', path: '/difference/quality' },
      { label: 'Partnerships', path: '/difference/partnerships' },
      { label: 'Projects', path: '/difference/projects' },
    ]
  },
  */
  { label: 'LineCard', path: 'https://drive.google.com/file/d/1_Ol2Ee_4fbg8jO4UbGYlYfv0KJ4v-ohB/view?usp=sharing' },
  { label: 'Contact', path: '/contact' },
];

const HeaderContainer = styled.header`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.darkest};
  box-shadow: ${({ theme }: { theme: DefaultTheme }) => theme.shadows.small};
  
  --breakpoint-lg: ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.lg};
`;

const TopBar = styled.div`
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.light};
  padding: 0.3rem 0;
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  .contact-info {
    display: flex;
    align-items: center;
  }

  a {
    color: ${({ theme }) => theme.colors.light};
    margin-left: 1.5rem;
    display: flex;
    align-items: center;
    
    &:hover {
      color: ${({ theme }) => theme.colors.secondary};
    }
    
    svg {
      margin-right: 0.5rem;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    
    .contact-info {
        justify-content: center;
        width: 100%;
    }

    a {
      margin: 0 0.75rem;
    }
  }
`;


const MainHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizes.xxlarge};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.light};
  
  span.elite {
    color: ${({ theme }) => theme.colors.light};
  }
  
  span.wire {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  .logo-shape {
    margin-right: 0.5rem;
    height: 40px;
    
    .diamond-top {
      fill: ${({ theme }) => theme.colors.secondary};
    }
    
    .diamond-bottom {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.light};
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: block;
  }
`;

const NavContainer = styled.nav<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: fixed;
    top: 0;
    right: ${({ isOpen }) => (isOpen ? '0' : '-300px')};
    width: 300px;
    height: 100vh;
    background-color: ${({ theme }) => theme.colors.darker};
    box-shadow: ${({ isOpen }) => (isOpen ? '-5px 0 15px rgba(0, 0, 0, 0.3)' : 'none')};
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
    transition: ${({ theme }) => theme.transitions.medium};
    overflow-y: auto;
    
    .close-button {
      align-self: flex-end;
      margin-bottom: 1rem;
    }
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavMenuItem = styled.li`
  position: relative;
  margin: 0 0.75rem;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    &:hover .submenu {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
    
    &:hover .dropdown-icon {
      transform: rotate(180deg);
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin: 0;
    width: 100%;
    
    &:hover .submenu {
      opacity: 0;
      visibility: hidden;
    }
  }
`;

const NavLink = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0rem 0.5rem;
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.light};
  background: none;
  border: none;
  cursor: pointer;
  font-weight: ${({ isActive }) => (isActive ? '700' : '500')};
  position: relative;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${({ isActive }) => (isActive ? '100%' : '0')};
    height: 3px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: ${({ theme }) => theme.transitions.medium};
  }
  
  &:hover::after {
    width: 100%;
  }
  
  .dropdown-icon {
    margin-left: 8px;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    transition: transform 0.3s ease;
    
    &.open {
      transform: rotate(180deg);
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    justify-content: space-between;
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGray};
    
    &::after {
      display: none;
    }
    
    .dropdown-icon {
      margin-left: 12px;
    }
  }
`;

const RouterLink = styled(Link) <{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.light};
  font-weight: ${({ isActive }) => (isActive ? '700' : '500')};
  position: relative;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${({ isActive }) => (isActive ? '100%' : '0')};
    height: 3px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: ${({ theme }) => theme.transitions.medium};
  }
  
  &:hover::after {
    width: 100%;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGray};
    
    &::after {
      display: none;
    }
  }
`;

const ExternalRouterLink = styled.a<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.light};
  font-weight: ${({ isActive }) => (isActive ? '700' : '500')};
  position: relative;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  text-decoration: none;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${({ isActive }) => (isActive ? '100%' : '0')};
    height: 3px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: ${({ theme }) => theme.transitions.medium};
  }
  
  &:hover::after {
    width: 100%;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGray};
    
    &::after {
      display: none;
    }
  }
`;

const SubMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: ${({ theme }) => theme.colors.darker};
  min-width: 220px;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: ${({ theme }) => theme.transitions.medium};
  z-index: 100;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: static;
    box-shadow: none;
    width: 100%;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    transform: none;
    margin-left: 1rem;
  }
`;

const SubMenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
`;

const SubMenuItem = styled.li`
  padding: 0;
  margin: 0;
`;

const SubMenuLink = styled(Link)`
  display: block;
  padding: 0.75rem 1.25rem;
  color: ${({ theme }) => theme.colors.light};
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.dark};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: 1rem;
  }
`;

const QuoteButtonContainer = styled.div`
  position: relative;
`;

const ContactLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.light};
  font-weight: 500;
  position: relative;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  padding: 0.2rem 0.5rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100%;
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGray};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NavLinkContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState<{ [key: string]: boolean }>({});
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check if we're on mobile
  useEffect(() => {
    const checkIsMobile = () => {
      const breakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-lg') || '992', 10);
      setIsMobile(window.innerWidth < breakpoint);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Close mobile menu when changing routes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (isMobileMenuOpen && !target.closest('nav') && !target.closest('button')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleSubMenu = (label: string) => {
    if (isMobile) {
      setOpenSubMenus((prev) => ({
        ...prev,
        [label]: !prev[label],
      }));
    }
  };

  const isPathActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <HeaderContainer>
      <TopBar>
        <TopBarContent>
          <MetalPriceTicker />
          <div className="contact-info">
            <a href="tel:+18473769187">
              <IconWrapper icon={FaPhone} size={14} /> +1 847 376 9187
            </a>
            <a href="mailto:info@electrocableus.com">
              <IconWrapper icon={FaEnvelope} size={14} /> info@electrocableus.com
            </a>
          </div>
        </TopBarContent>
      </TopBar>

      <MainHeader>
        <LogoContainer to="/">
          <Logo />
        </LogoContainer>

        <MobileMenuToggle onClick={toggleMobileMenu}>
          {isMobileMenuOpen ?
            <IconWrapper icon={FaTimes} size={24} /> :
            <IconWrapper icon={FaBars} size={24} />
          }
        </MobileMenuToggle>

        <NavContainer isOpen={isMobileMenuOpen}>
          {isMobileMenuOpen && (
            <button className="close-button" onClick={() => setIsMobileMenuOpen(false)}>
              <IconWrapper icon={FaTimes} size={24} />
            </button>
          )}

          <NavMenu>
            {MAIN_NAV_ITEMS.map((item) => (
              <NavMenuItem key={item.label}>
                {item.children ? (
                  <>
                    <NavLink
                      isActive={isPathActive(item.path)}
                      onClick={() => toggleSubMenu(item.label)}
                    >
                      {item.label}
                      <span className={`dropdown-icon ${openSubMenus[item.label] ? 'open' : ''}`}>
                        <IconWrapper icon={FaChevronDown} size={12} />
                      </span>
                    </NavLink>

                    <SubMenu
                      className="submenu"
                      isOpen={!!openSubMenus[item.label]}
                    >
                      <SubMenuList>
                        {item.children.map((child) => (
                          <SubMenuItem key={child.label}>
                            <SubMenuLink to={child.path}>{child.label}</SubMenuLink>
                          </SubMenuItem>
                        ))}
                      </SubMenuList>
                    </SubMenu>
                  </>
                ) : (
                  item.label === 'Contact' ? (
                    <ContactLink to={item.path}>
                      {item.label}
                    </ContactLink>
                  ) : item.label === 'Line Card' ? (
                    <ExternalRouterLink
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(item.path, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {item.label}
                    </ExternalRouterLink>
                  ) : (
                    <RouterLink to={item.path} isActive={isPathActive(item.path)}>
                      {item.label}
                    </RouterLink>
                  )
                )}
              </NavMenuItem>
            ))}
          </NavMenu>

          <NavActions>
            <QuoteButtonContainer>
              <Button as={Link} to="/request-quote" variant="primary" size="small">
                Request Quote
              </Button>
              <QuoteBadge />
            </QuoteButtonContainer>
          </NavActions>
        </NavContainer>
      </MainHeader>
    </HeaderContainer>
  );
};

export default Header;